/** Read-only per-server leg state of the Xaccel tab (GET /linear/:id/legs): labels, colours, ages. */
import { serverTypeColor, serverTypeLabel } from './server-type';

export const LEG_VERDICTS = ['ok', 'missing', 'orphan', 'adoptable', 'conflict', 'stale-name', 'unknown'] as const;
export type LegVerdict = typeof LEG_VERDICTS[number];

const KEY: Record<LegVerdict, string> = {
  ok: 'ok', missing: 'missing', orphan: 'orphan', adoptable: 'adoptable', conflict: 'conflict', 'stale-name': 'staleName', unknown: 'unknown',
};
const CLASS: Record<LegVerdict, string> = {
  ok: 'badge-success',
  missing: 'badge-error',
  orphan: 'badge-warning',
  adoptable: 'badge-info',
  conflict: 'badge-error',
  'stale-name': 'badge-warning',
  unknown: 'badge-neutral',
};

function known(v: string): LegVerdict {
  return (LEG_VERDICTS as readonly string[]).includes(v) ? (v as LegVerdict) : 'unknown';
}

export function verdictLabelKey(v: string): string {
  return `linear.legs.verdict.${KEY[known(v)]}`;
}
export function verdictHintKey(v: string): string {
  return `linear.legs.verdictHint.${KEY[known(v)]}`;
}
export function verdictClass(v: string): string {
  return CLASS[known(v)];
}

export function seenAgo(ageMs: number | null | undefined): { key: string; count?: number } {
  if (ageMs === null || ageMs === undefined) return { key: 'linear.legs.neverSeen' };
  if (ageMs < 60_000) return { key: 'linear.legs.seenJustNow' };
  if (ageMs < 3600_000) return { key: 'linear.legs.seenMinutes', count: Math.floor(ageMs / 60_000) };
  return { key: 'linear.legs.seenHours', count: Math.floor(ageMs / 3600_000) };
}

export interface LegReal { status: 'running' | 'stopped' | 'error' | 'unknown'; uptime?: number; clients?: number; ref: string }

export function realStatusKey(real: LegReal | 'missing' | 'unknown'): string {
  if (real === 'missing') return 'linear.legs.real.missing';
  if (real === 'unknown') return 'linear.legs.real.unknown';
  return ['running', 'stopped', 'error'].includes(real.status) ? `linear.legs.real.${real.status}` : 'linear.legs.real.unknown';
}

export function serverStateKey(s: { reachable: boolean; stateAgeMs: number | null }): string {
  if (s.reachable) return 'linear.legs.reachable';
  return s.stateAgeMs === null ? 'linear.legs.unreachableUnknown' : 'linear.legs.unreachableCached';
}

/**
 * When the panel reads the servers: once when it opens ('initial', a recent server snapshot is fine) and again with a
 * fresh read only after a real action changed something ('refresh', the page bumps a tick). A reload of the old list
 * or a re-render never reads again.
 */
export function legReloadMode(prevTick: number | null, tick: number): 'initial' | 'refresh' | 'none' {
  if (prevTick === null) return 'initial';
  return prevTick === tick ? 'none' : 'refresh';
}

/** Badge of a server: the shared helper for known types; a server the API could not type is 'unknown' (neutral). */
export function serverTypeBadge(type: string): { label?: string; labelKey?: string; className: string } {
  if (type === 'unknown') return { labelKey: 'linear.legs.typeUnknown', className: 'bg-dark-800 text-dark-400 border-dark-600' };
  return { label: serverTypeLabel(type), className: serverTypeColor(type) };
}

/** Badge of a held leg (nothing in the background starts it): stopped by the user, or no longer selected. */
export function heldBadge(leg: { held?: boolean; heldReason?: 'user-stop' | 'unwanted' } | null | undefined): { labelKey: string; hintKey: string } | null {
  if (!leg?.held) return null;
  const k = leg.heldReason === 'unwanted' ? 'unwanted' : 'userStop';
  return { labelKey: `linear.legs.held.${k}`, hintKey: `linear.legs.heldHint.${k}` };
}
